import { useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { EmailEntryPanel } from '@/customer/components/auth/email-entry-panel'
import { OtpPanel } from '@/customer/components/auth/otp-panel'
import {
  VERIFICATION_SUCCESS_HOLD_MS,
  VerificationSuccessPanel,
} from '@/customer/components/auth/verification-success-panel'
import { PageContainer } from '@/customer/components/page-container'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { meQueryKey, useGetMe } from '@/customer/hooks/use-get-me'
import { authClient, isAuthError } from '@/customer/lib/auth'
import { routes } from '@/customer/routes'

type Step = 'entry' | 'otp' | 'success'

/**
 * Change-email flow using better-auth's OTP-based `requestEmailChange` +
 * `changeEmail` pair. Verify-first by design - `members.email` is only
 * updated once the OTP against the new address succeeds, so an
 * abandoned attempt can never lock the member out of their account.
 */
export function EmailUpdatePage() {
  const homeUrl = routes.account.index
  useDocumentTitle('Update your email')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const me = useGetMe()

  // Better-auth's email-OTP plugin auto-refreshes the client session
  // signal on `/verify-email`, `/sign-in/email-otp`, and
  // `/request-email-change` - but not on `/change-email` where the
  // atomic swap actually happens. Grab `refetch` from the session hook
  // so we can nudge it manually on success.
  const { refetch: refetchSession } = authClient.useSession()

  const [step, setStep] = useState<Step>('entry')
  const [pendingEmail, setPendingEmail] = useState<string | null>(null)
  const [entryErrorCode, setEntryErrorCode] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function handleEntrySubmit(newEmail: string) {
    setIsSubmitting(true)
    setEntryErrorCode(null)
    const { data, error } = await authClient.emailOtp.requestEmailChange({ newEmail })
    setIsSubmitting(false)
    if (error) {
      setEntryErrorCode(isAuthError(error) ? error.code : 'UNKNOWN')
      return
    }
    if (!data) {
      setEntryErrorCode('UNKNOWN')
      return
    }
    setPendingEmail(newEmail)
    setStep('otp')
  }

  function goBackToEntry() {
    setPendingEmail(null)
    setEntryErrorCode(null)
    setStep('entry')
  }

  return (
    <PageContainer
      backLink={
        step === 'otp'
          ? { onClick: goBackToEntry, label: 'Use a different email' }
          : step === 'entry'
            ? { to: homeUrl, label: 'Back to account' }
            : undefined
      }
      headline={[
        { text: 'Update your', color: 'yellow' },
        { text: 'email address', color: 'blue' },
      ]}
    >
      <AnimatePresence mode="wait">
        {step === 'success' ? (
          <motion.div key="success">
            <VerificationSuccessPanel active />
          </motion.div>
        ) : step === 'otp' && pendingEmail ? (
          <motion.div key="otp" exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
            <EmailChangeOtp
              pendingEmail={pendingEmail}
              onSuccess={() => {
                queryClient.invalidateQueries({ queryKey: meQueryKey })
                void refetchSession()
                setStep('success')
                window.setTimeout(() => navigate(homeUrl), VERIFICATION_SUCCESS_HOLD_MS)
              }}
            />
          </motion.div>
        ) : (
          <motion.div key="entry">
            <EmailEntryPanel
              currentValue={me.data?.member.email}
              errorCode={entryErrorCode}
              isSubmitting={isSubmitting}
              onSubmit={handleEntrySubmit}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </PageContainer>
  )
}

function EmailChangeOtp({
  pendingEmail,
  onSuccess,
}: {
  pendingEmail: string
  onSuccess: () => void
}) {
  return (
    <OtpPanel
      onSuccess={onSuccess}
      sendCode={async () => {
        const { data, error } = await authClient.emailOtp.requestEmailChange({
          newEmail: pendingEmail,
        })
        if (error) throw error
        if (!data) throw new Error('Missing data')
      }}
      verifyCode={async (code) => {
        const { error } = await authClient.emailOtp.changeEmail({
          newEmail: pendingEmail,
          otp: code,
        })
        if (error) throw error
      }}
      blurb={
        <p className="text-body-md text-foreground">
          We&apos;ve sent a 6-digit code to <span className="font-semibold">{pendingEmail}</span>.
          Enter it below to confirm your new email address.
        </p>
      }
    />
  )
}
